import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, CheckCircle, AlertCircle, Database, Users, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';

const RefreshAction = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);

  const refreshComponents = [
    { name: 'User Data', icon: Users, status: 'pending' },
    { name: 'Analytics', icon: BarChart3, status: 'pending' },
    { name: 'Database', icon: Database, status: 'pending' },
    { name: 'System Health', icon: CheckCircle, status: 'pending' },
  ];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshStatus('starting');

    try {
      for (let i = 0; i < refreshComponents.length; i++) {
        setRefreshStatus(`refreshing-${i}`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setRefreshStatus('completed');
      toast.success('Dashboard refreshed successfully');

      setTimeout(() => {
        setRefreshStatus(null);
      }, 2000);
    } catch (error) {
      setRefreshStatus('error');
      toast.error('Failed to refresh dashboard: ' + error.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusIcon = index => {
    if (!refreshStatus) return null;

    if (refreshStatus === 'starting') {
      return <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />;
    }

    if (refreshStatus.startsWith('refreshing-')) {
      const currentIndex = parseInt(refreshStatus.split('-')[1]);
      if (index <= currentIndex) {
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />;
      }
    }

    if (refreshStatus === 'completed') {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }

    if (refreshStatus === 'error') {
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    }

    return null;
  };

  const getStatusText = () => {
    if (!refreshStatus) return 'Refresh Dashboard';
    if (refreshStatus === 'starting') return 'Starting refresh...';
    if (refreshStatus.startsWith('refreshing-')) return 'Refreshing components...';
    if (refreshStatus === 'completed') return 'Refresh completed';
    if (refreshStatus === 'error') return 'Refresh failed';
    return 'Refresh Dashboard';
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleRefresh}
      disabled={isRefreshing}
      className="flex flex-col items-center rounded-lg bg-gradient-to-br from-orange-50 via-orange-100 to-orange-200 p-3 shadow-md transition-colors hover:bg-orange-200 disabled:opacity-50 md:p-4"
    >
      <span className="mb-1.5 md:mb-2">
        {isRefreshing ? (
          <RefreshCw className="h-5 w-5 animate-spin text-orange-700 md:h-6 md:w-6" />
        ) : (
          <RefreshCw className="h-5 w-5 text-orange-700 md:h-6 md:w-6" />
        )}
      </span>
      <span className="text-xs font-medium text-orange-900 md:text-sm">{getStatusText()}</span>

      {isRefreshing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -bottom-16 left-1/2 min-w-48 -translate-x-1/2 transform rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
        >
          <div className="space-y-2">
            {refreshComponents.map((component, index) => {
              const IconComponent = component.icon;
              const statusIcon = getStatusIcon(index);

              return (
                <div key={component.name} className="flex items-center gap-2 text-xs">
                  <IconComponent className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-700">{component.name}</span>
                  {statusIcon}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </motion.button>
  );
};

export default RefreshAction;
